/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { isEmpty } from 'lodash';
import { css, StyleSheet } from 'aphrodite';

import { Header } from '@blend/enterprise-components';
import { AlertBar } from '@blend/enterprise-components';
import { LoadingSpinner } from '@blend/enterprise-components';

import { getLoans } from 'blb-api/loans';
import { MeContext } from 'blb-contexts/Me';
import { searchWidth } from 'blb-styles/constants';
import { BlendBridge } from 'blb-types/blendBridge';
import { errorMessage } from 'blb-utils/errors';

import { PipelineSearch } from './PipelineSearch';
import { PipelineTable } from './PipelineTable';

export const styles = StyleSheet.create({
  centeredContainer: {
    alignItems: 'center',
    display: 'flex',
    height: '100%',
    justifyContent: 'center',
    position: 'absolute',
    width: '100%',
  },
  errorContainer: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    position: 'absolute',
    top: '15%',
    width: '100%',
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    padding: `42px 40px`,
  },
  pipelineContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: `100%`,
  },
  searchWrapper: {
    width: searchWidth,
  },
});

const { useState, useEffect, useContext } = React;

export namespace PipelinePage {
  export interface Props {}
}

export const PipelinePage: React.FC<PipelinePage.Props> = ({}) => {
  const me = useContext(MeContext);
  const [loans, setLoans] = useState<BlendBridge.API.Loan[]>([]);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const hasNoLoans = isEmpty(loans);

  const fetchLoans = async () => {
    try {
      setLoading(true);
      const { data: fetchedLoans } = await getLoans();
      setLoans(fetchedLoans);
    } catch (error) {
      setHasError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    fetchLoans();
  }, []);

  const getTableStatus = () => {
    if (isLoading) {
      return <LoadingSpinner size="lg" theme="secondary" />;
    }
    if (hasNoLoans) {
      return (
        <Header id="no-loan-message" size={2}>
          No loans here at the moment.
        </Header>
      );
    }
  };

  return (
    <>
      <div className={css(styles.pipelineContainer)} data-it="pipeline">
        <div className={css(styles.header)} id="it-Pipeline-header">
          <Header size={2}>Loans</Header>
          <div className={css(styles.searchWrapper)}>
            <PipelineSearch loans={loans} />
          </div>
        </div>
        <PipelineTable loans={loans} />
        {hasError && (
          <div className={css(styles.errorContainer)} id="error-message">
            <AlertBar icon="error" message={errorMessage('FETCHING_PIPELINE', me?.principalType)} theme="error" />
          </div>
        )}
        {!!getTableStatus() && <div className={css(styles.centeredContainer)}>{getTableStatus()}</div>}
      </div>
    </>
  );
};
